import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { AccountStatus, ProgrammeStatus } from '@dealstack/shared/lib/generated';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ColumnStatusDefinition } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/StatusColumn.types.ts';

export const getAccountStatusLabel = (
	status: string,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): string => {
	switch (status) {
		case AccountStatus.NOT_INVITED:
			return tMipManage('investors.table.account_status.not_invited');
		case AccountStatus.INIVITED:
			return tMipManage('investors.table.account_status.invited');
		case AccountStatus.ACTIVE:
			return tMipManage('investors.table.account_status.active');
		default:
			return status;
	}
};

export const getProgrammeStatusLabel = (
	status: string,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): string => {
	switch (status) {
		case ProgrammeStatus.JOINER:
			return tMipManage('investors.table.programme_status.joiner');
		case ProgrammeStatus.HOLDER:
			return tMipManage('investors.table.programme_status.holder');
		case ProgrammeStatus.LEAVER:
			return tMipManage('investors.table.programme_status.leaver');
		case ProgrammeStatus.INACTIVE:
			return tMipManage('investors.table.programme_status.inactive');
		default:
			return status;
	}
};

export const getAccountStatusFilterOptions = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) =>
	Object.values(AccountStatus).map((status) => ({
		label: getAccountStatusLabel(status, tMipManage),
		value: status,
	}));

export const getProgrammeStatusFilterOptions = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) =>
	Object.values(ProgrammeStatus).map((status) => ({
		label: getProgrammeStatusLabel(status, tMipManage),
		value: status,
	}));

export const PROGRAMME_STATUS_COLORS: Record<string, { color: string; textColor?: string }> = {
	[ProgrammeStatus.JOINER]: { color: 'blue.6' },
	[ProgrammeStatus.HOLDER]: { color: 'teal.8' },
	[ProgrammeStatus.LEAVER]: { color: 'orange.6' },
	[ProgrammeStatus.INACTIVE]: { color: 'gray.4', textColor: 'dark' },
};

export const ACCOUNT_STATUS_COLORS: Record<string, { color: string; textColor?: string }> = {
	[AccountStatus.NOT_INVITED]: { color: 'gray.4', textColor: 'dark' },
	[AccountStatus.INIVITED]: { color: 'orange.6' },
	[AccountStatus.ACTIVE]: { color: 'teal.8' },
};

const resolveColor = (mantineColor: string): string => {
	const [name, shade] = mantineColor.split('.');
	return defaultTheme.v3.colors[name]?.[Number(shade)] ?? mantineColor;
};

export const getAccountStatusDefinition = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): ColumnStatusDefinition[] =>
	Object.values(AccountStatus).map((status) => ({
		status,
		color: resolveColor(ACCOUNT_STATUS_COLORS[status]?.color ?? 'gray.4'),
		text: getAccountStatusLabel(status, tMipManage),
	}));

export const getProgrammeStatusDefinition = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): ColumnStatusDefinition[] =>
	Object.values(ProgrammeStatus).map((status) => ({
		status,
		color: resolveColor(PROGRAMME_STATUS_COLORS[status]?.color ?? 'gray.4'),
		text: getProgrammeStatusLabel(status, tMipManage),
	}));
